import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PublicIcon from '@mui/icons-material/Public';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useGlobalState } from '../global/context/global-context';
import PageHeader from '../components/shared/page-header';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`city-info-tabpanel-${index}`}
      aria-labelledby={`city-info-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 4 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `city-info-tab-${index}`,
    'aria-controls': `city-info-tabpanel-${index}`,
  };
}

export function CityInfoComponent() {
  const { parametersPortal } = useGlobalState();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Define available sections based on what data exists
  const availableSections = useMemo(() => {
    if (!parametersPortal) return [];

    const sections = [
      {
        key: 'history',
        label: 'História',
        icon: <HistoryEduIcon />,
        content: parametersPortal.history,
      },
      {
        key: 'hymnn',
        label: 'Hino',
        icon: <MusicNoteIcon />,
        content: parametersPortal.hymnn,
      },
      {
        key: 'economy',
        label: 'Economia',
        icon: <AttachMoneyIcon />,
        content: parametersPortal.economy,
      },
      {
        key: 'geographicInformation',
        label: 'Informações Geográficas',
        icon: <PublicIcon />,
        content: parametersPortal.geographicInformation,
      },
      {
        key: 'institutional',
        label: 'Institucional',
        icon: <AccountBalanceIcon />,
        content: parametersPortal.institutional,
      },
    ];

    // Filter out sections with no content
    return sections.filter((section) => section.content);
  }, [parametersPortal]);

  // Loading state
  if (!parametersPortal) {
    return (
      <Box>
        <Skeleton variant="rounded" width="100%" height={500} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  // No content available
  if (availableSections.length === 0) {
    return (
      <Box>
        <PageHeader
          title="A Cidade"
          subtitle="Conheça mais sobre a história, economia e características do município."
          breadcrumbs={[{ label: 'A Cidade' }]}
        />
        <Paper
          elevation={2}
          sx={{
            p: 4,
            textAlign: 'center',
            minHeight: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Informações sobre a cidade não disponíveis no momento.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="A Cidade"
        subtitle="Conheça mais sobre a história, economia e características do município."
        breadcrumbs={[{ label: 'A Cidade' }]}
      />

      <Paper
        elevation={2}
        sx={{
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="city information tabs"
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&.Mui-selected': {
                  fontWeight: 600,
                },
              },
            }}
          >
            {availableSections.map((section, index) => (
              <Tab
                key={section.key}
                icon={section.icon}
                label={section.label}
                iconPosition="start"
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </Box>

        {availableSections.map((section, index) => (
          <TabPanel key={section.key} value={activeTab} index={index}>
            <Box
              sx={{
                // Remove font-family from all elements to keep portal standard
                '& *': {
                  fontFamily: 'inherit !important',
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  color: 'primary.main',
                  fontWeight: 600,
                  mb: 2,
                  mt: 3,
                  '&:first-of-type': {
                    mt: 0,
                  },
                },
                '& p': {
                  lineHeight: 1.8,
                  mb: 2,
                  color: 'text.primary',
                  fontSize: '1rem',
                },
                '& ul, & ol': {
                  lineHeight: 1.8,
                  mb: 2,
                  pl: 3,
                },
                '& li': {
                  mb: 1,
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 1,
                  my: 2,
                },
                '& a': {
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                },
                '& blockquote': {
                  borderLeft: 4,
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2,
                },
                '& th, & td': {
                  border: 1,
                  borderColor: 'divider',
                  p: 1.5,
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 600,
                },
              }}
              dangerouslySetInnerHTML={{ __html: section.content || '' }}
            />
          </TabPanel>
        ))}
      </Paper>
    </Box>
  );
}
